"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.forwardDocumentToState = forwardDocumentToState;
exports.forwardDocumentUpdate = forwardDocumentUpdate;
const Y = __importStar(require("yjs"));
function isRecord(value) {
    return value !== null && typeof value === 'object';
}
/**
 * Updates carry the type of nested values, not of root types. A root with
 * live keys is a map, a root whose live items hold strings or formats is a
 * text, any other root with live items is an array. A root without live
 * content has no kind: it reads as empty either way.
 */
function documentRootKind(root) {
    if (root instanceof Y.Map)
        return 'map';
    if (root instanceof Y.Text)
        return 'text';
    if (root instanceof Y.Array)
        return 'array';
    if (!(root instanceof Y.AbstractType))
        return null;
    for (const item of root._map.values())
        if (!item.deleted)
            return 'map';
    let kind = null;
    for (let item = root._start; item; item = item.right) {
        if (item.deleted)
            continue;
        if (item.content instanceof Y.ContentString ||
            item.content instanceof Y.ContentFormat)
            return 'text';
        kind = 'array';
    }
    return kind;
}
function rootOf(doc, name, kind) {
    switch (kind) {
        case 'map':
            return doc.getMap(name);
        case 'array':
            return doc.getArray(name);
        case 'text':
            return doc.getText(name);
    }
}
const isShared = (value) => value instanceof Y.Map ||
    value instanceof Y.Array ||
    value instanceof Y.Text ||
    value instanceof Y.XmlFragment;
const sameConstructor = (a, b) => a.constructor === b.constructor;
function cloneValue(value) {
    if (value instanceof Y.Map) {
        const clone = new Y.Map();
        for (const [key, entry] of value.entries())
            clone.set(key, cloneValue(entry));
        return clone;
    }
    if (value instanceof Y.Array) {
        const clone = new Y.Array();
        clone.insert(0, value.toArray().map(cloneValue));
        return clone;
    }
    if (value instanceof Y.XmlFragment) {
        const clone = value instanceof Y.XmlElement
            ? new Y.XmlElement(value.nodeName)
            : new Y.XmlFragment();
        if (value instanceof Y.XmlElement && clone instanceof Y.XmlElement)
            for (const [key, attribute] of Object.entries(value.getAttributes()))
                if (attribute !== undefined)
                    clone.setAttribute(key, attribute);
        const children = value.toArray().map(child => {
            const cloned = cloneValue(child);
            if (!(cloned instanceof Y.XmlElement) && !(cloned instanceof Y.XmlText))
                throw new Error('An unsupported XML child cannot be brought to an earlier state');
            return cloned;
        });
        clone.insert(0, children);
        return clone;
    }
    if (value instanceof Y.Text) {
        const clone = value instanceof Y.XmlText ? new Y.XmlText() : new Y.Text();
        const delta = value.toDelta();
        clone.applyDelta(delta.map(op => isShared(op.insert) ? { ...op, insert: cloneValue(op.insert) } : op));
        if (value instanceof Y.XmlText && clone instanceof Y.XmlText)
            for (const [key, attribute] of Object.entries(value.getAttributes()))
                clone.setAttribute(key, attribute);
        return clone;
    }
    if (value instanceof Y.AbstractType)
        throw new Error('Unsupported shared value in document history');
    if (value instanceof Y.Doc)
        throw new Error('A nested document cannot be brought to an earlier state');
    return structuredClone(value);
}
/** Equal content, including text formatting and the kind of shared values. */
function sameContent(a, b) {
    if (Object.is(a, b))
        return true;
    if (isShared(a) || isShared(b)) {
        if (!isShared(a) || !isShared(b) || !sameConstructor(a, b))
            return false;
        if (a instanceof Y.Map && b instanceof Y.Map) {
            if (a.size !== b.size)
                return false;
            for (const [key, value] of a.entries())
                if (!b.has(key) || !sameContent(value, b.get(key)))
                    return false;
            return true;
        }
        if (a instanceof Y.Array && b instanceof Y.Array)
            return sameContent(a.toArray(), b.toArray());
        if (a instanceof Y.XmlElement && b instanceof Y.XmlElement)
            return (a.nodeName === b.nodeName &&
                sameContent(a.getAttributes(), b.getAttributes()) &&
                sameContent(a.toArray(), b.toArray()));
        if (a instanceof Y.XmlFragment && b instanceof Y.XmlFragment)
            return sameContent(a.toArray(), b.toArray());
        if (a instanceof Y.XmlText && b instanceof Y.XmlText)
            return (sameContent(a.toDelta(), b.toDelta()) &&
                sameContent(a.getAttributes(), b.getAttributes()));
        if (a instanceof Y.Text && b instanceof Y.Text)
            return sameContent(a.toDelta(), b.toDelta());
        return a.toString() === b.toString();
    }
    if (a instanceof Y.Doc || b instanceof Y.Doc)
        return a instanceof Y.Doc && b instanceof Y.Doc && a.guid === b.guid;
    if (a instanceof Uint8Array || b instanceof Uint8Array)
        return (a instanceof Uint8Array &&
            b instanceof Uint8Array &&
            a.length === b.length &&
            a.every((byte, index) => byte === b[index]));
    if (Array.isArray(a) || Array.isArray(b))
        return (Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((value, index) => sameContent(value, b[index])));
    if (isRecord(a) && isRecord(b)) {
        const keys = Object.keys(a);
        if (keys.length !== Object.keys(b).length)
            return false;
        return keys.every(key => Object.prototype.hasOwnProperty.call(b, key) &&
            sameContent(a[key], b[key]));
    }
    return false;
}
/** Brings a shared value in place to the content of one of the same type. */
function forwardShared(current, target) {
    if (!sameConstructor(current, target))
        return false;
    // XML nodes carry attributes beside their content: replaced as a whole.
    if (current instanceof Y.XmlText || current instanceof Y.XmlFragment)
        return sameContent(current, target);
    if (current instanceof Y.Map && target instanceof Y.Map)
        forwardMap(current, target);
    else if (current instanceof Y.Array && target instanceof Y.Array)
        forwardArray(current, target);
    else if (current instanceof Y.Text && target instanceof Y.Text)
        forwardText(current, target);
    else
        return sameContent(current, target);
    return true;
}
function forwardMap(current, target) {
    for (const key of [...current.keys()])
        if (!target.has(key))
            current.delete(key);
    for (const [key, value] of target.entries()) {
        const existing = current.get(key);
        if (current.has(key) && sameContent(existing, value))
            continue;
        if (isShared(existing) && isShared(value) && forwardShared(existing, value))
            continue;
        current.set(key, cloneValue(value));
    }
}
/** A ContentAny item can span several array entries; each has its own clock. */
function arrayItemIds(array) {
    const ids = [];
    for (let item = array._start; item; item = item.right) {
        if (item.deleted || !item.countable)
            continue;
        for (let offset = 0; offset < item.length; offset++)
            ids.push(`${item.id.client}:${item.id.clock + offset}`);
    }
    return ids;
}
/**
 * Common live item IDs anchor the historical sequence in linear time. Edits
 * beside a nested shared value must not delete it: offline edits still refer
 * to that exact CRDT item. Gaps reconcile positionally when equally sized.
 */
function forwardArray(current, target) {
    const from = current.toArray();
    const to = target.toArray();
    const fromIds = arrayItemIds(current);
    const toIds = arrayItemIds(target);
    let start = 0;
    while (start < from.length &&
        start < to.length &&
        sameContent(from[start], to[start]))
        start++;
    let end = 0;
    while (end < from.length - start &&
        end < to.length - start &&
        sameContent(from[from.length - 1 - end], to[to.length - 1 - end]))
        end++;
    function reconcileValue(index, existing, value) {
        if (sameContent(existing, value))
            return;
        if (isShared(existing) && isShared(value) && forwardShared(existing, value))
            return;
        current.delete(index, 1);
        current.insert(index, [cloneValue(value)]);
    }
    const positions = new Map();
    for (let index = start; index < from.length - end; index++)
        positions.set(fromIds[index], index);
    let fromStart = start;
    let toStart = start;
    function reconcileGap(fromEnd, toEnd) {
        const removed = fromEnd - fromStart;
        const inserted = toEnd - toStart;
        if (removed === inserted) {
            for (let offset = 0; offset < removed; offset++)
                reconcileValue(toStart + offset, from[fromStart + offset], to[toStart + offset]);
        }
        else {
            if (removed)
                current.delete(toStart, removed);
            if (inserted)
                current.insert(toStart, to.slice(toStart, toEnd).map(cloneValue));
        }
    }
    for (let toIndex = start; toIndex < to.length - end; toIndex++) {
        const fromIndex = positions.get(toIds[toIndex]);
        if (fromIndex === undefined || fromIndex < fromStart)
            continue;
        reconcileGap(fromIndex, toIndex);
        reconcileValue(toIndex, from[fromIndex], to[toIndex]);
        fromStart = fromIndex + 1;
        toStart = toIndex + 1;
    }
    reconcileGap(from.length - end, to.length - end);
}
const plainText = (delta) => delta.every(op => typeof op.insert === 'string' && !op.attributes);
function splitsSurrogatePair(text, index) {
    const left = text.charCodeAt(index - 1);
    const right = text.charCodeAt(index);
    return left >= 0xd800 && left <= 0xdbff && right >= 0xdc00 && right <= 0xdfff;
}
/**
 * Plain text keeps its equal start and end; formatted text or embeds are
 * written as a whole. Text typed elsewhere at the same time stays in either
 * case, anchored to the characters it was typed next to.
 */
function forwardText(current, target) {
    const from = current.toDelta();
    const to = target.toDelta();
    if (sameContent(from, to))
        return;
    if (plainText(from) && plainText(to)) {
        const a = current.toString();
        const b = target.toString();
        let start = 0;
        while (start < a.length && start < b.length && a[start] === b[start])
            start++;
        // Y.Text uses UTF-16 offsets, but splitting a pair corrupts both halves.
        if (splitsSurrogatePair(a, start) || splitsSurrogatePair(b, start))
            start--;
        let end = 0;
        while (end < a.length - start &&
            end < b.length - start &&
            a[a.length - 1 - end] === b[b.length - 1 - end])
            end++;
        if (splitsSurrogatePair(a, a.length - end) ||
            splitsSurrogatePair(b, b.length - end))
            end--;
        if (a.length - start - end)
            current.delete(start, a.length - start - end);
        if (b.length - start - end)
            current.insert(start, b.slice(start, b.length - end));
        return;
    }
    current.delete(0, current.length);
    current.applyDelta(to.map(op => isShared(op.insert) ? { ...op, insert: cloneValue(op.insert) } : op));
}
function clearRoot(doc, name, kind) {
    const root = rootOf(doc, name, kind);
    if (root instanceof Y.Map)
        for (const key of [...root.keys()])
            root.delete(key);
    else if (root.length)
        root.delete(0, root.length);
}
/**
 * Brings `doc` to the content of `target` with new changes on `doc`, never by
 * replacing its history. Only differing parts change: unchanged entries keep
 * their CRDT items, so changes made elsewhere at the same time, offline or
 * not, still merge into the result.
 */
function forwardDocumentToState(doc, target) {
    doc.transact(() => {
        const targetKinds = new Map();
        for (const [name, root] of target.share) {
            const kind = documentRootKind(root);
            if (kind)
                targetKinds.set(name, kind);
        }
        for (const [name, root] of [...doc.share]) {
            const kind = documentRootKind(root);
            if (kind && !targetKinds.has(name))
                clearRoot(doc, name, kind);
        }
        for (const [name, kind] of targetKinds)
            forwardShared(rootOf(doc, name, kind), rootOf(target, name, kind));
    });
}
/**
 * The update that brings the state `current` to the content of `target`;
 * empty when both already hold the same content.
 */
function forwardDocumentUpdate(current, target) {
    const doc = new Y.Doc();
    const targetDoc = new Y.Doc();
    try {
        if (current.byteLength)
            Y.applyUpdate(doc, current);
        Y.applyUpdate(targetDoc, target);
        const changes = [];
        doc.on('update', (update) => changes.push(update));
        forwardDocumentToState(doc, targetDoc);
        if (changes.length === 0)
            return new Uint8Array();
        return changes.length === 1 ? changes[0] : Y.mergeUpdates(changes);
    }
    finally {
        targetDoc.destroy();
        doc.destroy();
    }
}
